import { createSelector } from '@reduxjs/toolkit';
import { format, subYears } from 'date-fns';

import {
  formattedEligibilityDates,
  getIEPStatus,
} from 'lib/eligibilityFormulas';

import {
  selectPartnerName,
  selectPartnerPhone,
} from './convenienceVariablesSlice';
import { selectAvailabilityState } from './availabilitySlice';
import { selectDynamicValues } from './dynamicSlice';
import {
  selectSEPEligibility,
  selectEligibilityCase,
} from './eligibilitySlice';
import { selectNumberOfPrescriptions } from './prescriptionSlice';
import { selectMaPlansAvailable } from './recommendationSlice';
import { selectBirthday, selectUser } from './userSlice';
import { selectCurrentLocation } from './locationSlice';
import {
  selectAmountSavedHMO,
  selectBestPlanOption,
  selectBirthdate,
  selectCostUnexpectedExpense,
  selectEligibleCoverageTypes,
  selectHasMedSuppInterest,
  selectHighCostMedSupp,
  selectIsUserLoggedIn,
  selectLowCostHMO,
  selectLowCostMedSupp,
  selectMedicareSupEligibilityInformation,
} from './selectors';
import { wholeDollarFormatter } from 'utils/formatters';
import {
  resolveAvailabilityCountsForLocation,
  resolveAvailabilityYearForDynamicCopy,
} from 'lib/resolveAvailabilityCounts';
import { NEXT_PLAN_YEAR, PLAN_YEAR } from 'lib/planYears';
import { parseYesNoValue } from 'utils/yesNo';

const selectFormattedEligibilityDates = createSelector(
  [selectBirthday],
  (birthDate) => formattedEligibilityDates(birthDate as string)
);

const selectIEPStatus = createSelector([selectBirthdate], (birthdate) =>
  getIEPStatus(birthdate)
);

const selectIsEligibleForMedSupp = createSelector(
  [selectIEPStatus],
  (status) => {
    return status !== 'pastIEP';
  }
);

const selectConvenienceVariables = createSelector(
  [selectIEPStatus],
  (iepStatus) => {
    return {
      iepStatus,
      planYear: String(PLAN_YEAR),
      nextPlanYear: String(NEXT_PLAN_YEAR),
      twoYearsAgo: format(subYears(new Date(), 2), 'yyyy'),
    };
  }
);

const selectEndDateSpecialEnrollment = createSelector(
  [selectSEPEligibility],
  (sepEligibility) => {
    // Return the enrollment end date from SEP form if available
    // Otherwise return empty string or default value
    return sepEligibility?.enrollmentEndDate || '';
  }
);

const selectInIepOrSep = createSelector(
  [selectEligibilityCase],
  (eligibilityCase) => {
    return (
      eligibilityCase === 'inIEP' ||
      eligibilityCase === 'inIEPAEP' ||
      eligibilityCase === 'inSEP'
    );
  }
);

export const selectUserDynamicValues = createSelector(
  [
    selectCurrentLocation,
    selectAvailabilityState,
    selectConvenienceVariables,
    selectDynamicValues,
    selectNumberOfPrescriptions,
    selectPartnerName,
    selectPartnerPhone,
    selectEndDateSpecialEnrollment,
    selectHighCostMedSupp,
    selectLowCostMedSupp,
    selectBestPlanOption,
    selectCostUnexpectedExpense,
    selectLowCostHMO,
    selectAmountSavedHMO,
    selectFormattedEligibilityDates,
    selectEligibleCoverageTypes,
    selectMaPlansAvailable,
    selectMedicareSupEligibilityInformation,
    selectUser,
    selectIsEligibleForMedSupp,
    selectIsUserLoggedIn,
    selectInIepOrSep,
    selectHasMedSuppInterest,
  ],
  (
    currentLocation,
    availability,
    convenienceVariables,
    dynamicValues,
    numberOfPrescriptions,
    partnerName,
    partnerPhone,
    endDateSpecialEnrollment,
    highCostMedSupp,
    lowCostMedSupp,
    bestPlanOption,
    costUnexpectedExpense,
    lowCostHMO,
    amountSavedHMO,
    eligibilityDates,
    eligibilityCoverageTypes,
    maPlansAvailable,
    medSupEligibilityInformation,
    user,
    isEligibleForMedSupp,
    isLoggedIn,
    inIepOrSep,
    hasMedSuppInterest
  ) => {
    const costDiff = bestPlanOption.difference;
    const yearAvailability = resolveAvailabilityYearForDynamicCopy(
      availability.byYear
    ) ?? {
      stateProductCounts: {},
      carrierCountsByState: {},
      medSuppEligibleStates: [],
    };
    const availabilityCounts = resolveAvailabilityCountsForLocation(
      yearAvailability.stateProductCounts,
      yearAvailability.carrierCountsByState,
      currentLocation?.state
    );

    return {
      birthdate: user?.birthdate || '',
      currentlyMarried: (() => {
        const fromSession = parseYesNoValue(dynamicValues?.currentlyMarried);
        if (fromSession !== null) {
          return fromSession;
        }
        return user?.currently_married ?? false;
      })(),
      income: user?.income || 0,
      usesTobacco: user?.tobacco === true ? 'yes' : 'no',
      zipCode: currentLocation?.zipcode || 'Not set',
      countyFips: currentLocation?.countyFips || 'Not set',
      usesHearingAids: user?.hearing_aids === true ? 'yes' : 'no',
      usesDentalOrVision: dynamicValues?.usesDentalOrVision || 'Not set',
      iepStatus: convenienceVariables?.iepStatus || 'pastIEP',
      isEligibleForMedSupp,
      planYear: String(PLAN_YEAR),
      nextPlanYear: String(NEXT_PLAN_YEAR),
      twoYearsAgo: convenienceVariables?.twoYearsAgo || '2023',
      partnerName: partnerName || 'Lig',
      partnerPhone: partnerPhone || '(833) 748-5302',
      sexAtBirth: user.gender || 'Not set',
      pcpVisits: user?.pcp_visits || 'Not set',
      specialistVisits: user?.specialist_visits || 'Not set',
      hospitalVisits: user?.hospital_visits || 'Not set',
      answeredYesToMedSuppQuestions: hasMedSuppInterest ? 'yes' : 'no',
      numberOfPrescriptions,
      endInitialEnrollmentDate: eligibilityDates?.iepEndDate || 'Not set',
      initialEligibilityDate:
        eligibilityDates?.firstCoverageStartDate || 'Not set',
      planEffectDate: eligibilityDates?.firstCoverageStartDate || 'Not set',
      postEligibilityDate: eligibilityDates?.iepEndDate || 'Not set',
      postEligibilityPlanEffectDate:
        eligibilityDates?.firstCoverageStartDate || 'Not set',
      endDateSpecialEnrollment,
      highCostMedSupp,
      medSuppEndDate:
        medSupEligibilityInformation?.eligibilityWindow?.endDate ||
        new Date().toISOString(),
      lowCostMedSupp,
      costUnexpectedExpense,
      lowCostHMO,
      amountSavedHMO,
      eligibilityCoverageTypes,
      maPlansAvailable,
      bestPlanOption: bestPlanOption.type,
      bestPlanCostDifference:
        costDiff === null
          ? '[none]'
          : wholeDollarFormatter.format(costDiff as number),
      bestPlanCostDifferenceMonthly:
        costDiff === null
          ? '[none]'
          : wholeDollarFormatter.format(Math.round((costDiff as number) / 12)),
      isLoggedIn,
      isBEQReady: Boolean(user?.birthdate) && Boolean(user?.medicare_number),
      inIepOrSep: inIepOrSep || 'Not set',
      eligibilityWebpage:
        '<a href="https://www.myalexhealth.com/lbry/ineligible-users" target="_blank">here</a>',
      medicareEducationCompletionCount:
        user?.medicare_education_completion_count ?? 0,
      carrierCount: availabilityCounts.carrierCount,
      productCount: availabilityCounts.productCount,
      maOnlyCount: availabilityCounts.maOnlyCount,
      maPdCount: availabilityCounts.maPdCount,
      maSubCount: availabilityCounts.maSubCount,
      pdpCount: availabilityCounts.pdpCount,
      medSupCount: availabilityCounts.medSupCount,
    };
  }
);
